import { isEmpty } from 'lodash';
import { useQuery, UseQueryResult } from 'react-query';

import { useAuth } from 'src/hooks';
import { getWidgetList } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TVariables = {
  token: string;
  tokenType: string;
};

type APIResult = {
  Data?: {
    'Dashboard-Settings'?: string;
  };
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = string[];

type TError = Error;

async function fetchWidgetsList({
  token,
  tokenType,
}: TVariables): Promise<APIResult> {
  const response = await fetch(getWidgetList, {
    method: 'GET',
    headers: {
      Authorization: `${tokenType} ${token}`,
    },
  });

  let data: APIResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useGetDashboardSettings(): UseQueryResult<TResult, TError> {
  const { token, tokenType } = useAuth();

  return useQuery(
    ['get_dashboard_settings'],
    async () => {
      const fetchResponse = await fetchWidgetsList({
        token,
        tokenType,
      });

      if (!fetchResponse.IsSuccess) {
        const errorMessage =
          fetchResponse.Errors?.map(err => err.Message).join(', ') ||
          'Failed to fetch dashboard settings';

        throw new Error(errorMessage);
      }

      const dashboardSettings = fetchResponse.Data?.['Dashboard-Settings'];

      if (!dashboardSettings || isEmpty(dashboardSettings)) {
        return [];
      }

      return dashboardSettings
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    },
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

export { useGetDashboardSettings };
